import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Controller, useForm } from 'react-hook-form';
import dayjs from '~/timeInit';

import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import { Input, Select } from '~/components/Common/Forms/fields';

import {
  useActivateCppRateCard,
  useCppPartners,
  useCreateCppRateCard,
  useUpdateCppRateCard,
} from '~/hooks/apis/costPerPoint';

import type { CppPartnerType, CppPricingModel, CppRateCard, CppRateTier } from '~/types/costPerPoint';

import { validateTiersClient } from './tierValidation';

interface RateCardDialogProps {
  open: boolean;
  onClose: () => void;
  /** null → create mode; otherwise edit mode with fields disabled per lifecycle state. */
  card: CppRateCard | null;
}

interface FormValues {
  partnerId: string;
  currency: string;
  pricingModel: CppPricingModel;
  effectiveStart: string;
  effectiveEnd: string;
}

/** A single unbounded tier used whenever the pricing model is FLAT. */
const FLAT_TIER: CppRateTier = { minPoints: 0, maxPoints: null, cpp: 0 };

/** Structured overlap-conflict payload the backend attaches to a CPP_OVERLAP_CONFLICT error. */
interface CppOverlapConflict {
  effectiveStart: string;
  effectiveEnd: string | null;
  reference?: string;
}

/** Narrows an unknown value to a CppOverlapConflict shape without resorting to `any`. */
function isCppOverlapConflict(value: unknown): value is CppOverlapConflict {
  return !!value && typeof value === 'object' && 'effectiveStart' in value;
}

/**
 * Extracts a clean message + optional conflicting-window detail from a submit error.
 * The service `request` helper wraps HTTP failures in an `ApiError` whose `message` is
 * prefixed with "Network error received (status code NNN): "; that prefix is stripped for
 * a cleaner first line. When the API returns structured overlap-conflict data (see
 * `CppOverlapConflict`), a second line with the conflicting card's effective date window is added.
 *
 * @param err - The thrown submit/activation error.
 * @param savedButActivateFailed - True when the draft was saved successfully but the subsequent
 *   activation failed; prefixes the message so the user knows their edits are already persisted
 *   and only activation needs to be retried (e.g. after resolving an overlap).
 */
function toSubmitError(err: unknown, savedButActivateFailed = false): { message: string; detail?: string } {
  const rawMessage = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
  // Strip the service helper's "Network error received (status code NNN): " prefix for a clean line.
  const cleanMessage = rawMessage.replace(/^Network error received \(status code \d+\):\s*/i, '');
  // Make it explicit that the draft is already saved and only activation failed, so the user
  // doesn't fear losing their work and understands a retry only re-attempts activation.
  const message = savedButActivateFailed ? `Draft saved, but activation failed: ${cleanMessage}` : cleanMessage;

  // If the API returned structured overlap-conflict data, add the conflicting card's date window.
  const errors = (err as { errors?: unknown }).errors;
  if (Array.isArray(errors)) {
    const conflict = errors
      .map((e) => (e && typeof e === 'object' ? (e as { conflict?: unknown }).conflict : undefined))
      .find(isCppOverlapConflict);
    if (conflict) {
      const start = dayjs(conflict.effectiveStart).format('DD MMM YYYY');
      const end = conflict.effectiveEnd ? dayjs(conflict.effectiveEnd).format('DD MMM YYYY') : 'Ongoing';
      return { message, detail: `Existing card effective ${start} – ${end}` };
    }
  }
  return { message };
}

/**
 * Determines which fields are editable given the card's lifecycle state:
 * - create (card === null) and DRAFT → all fields enabled.
 * - ACTIVE with a future effective start → all fields enabled (contract hasn't started yet).
 * - ACTIVE and already started → only the Effective End date remains editable.
 * - INACTIVE → fully read-only (view only).
 */
function editability(card: CppRateCard | null): { all: boolean; endOnly: boolean; readOnly: boolean } {
  if (!card) return { all: true, endOnly: false, readOnly: false };
  if (card.status === 'DRAFT') return { all: true, endOnly: false, readOnly: false };
  if (card.status === 'INACTIVE') return { all: false, endOnly: false, readOnly: true };
  // ACTIVE
  const started = dayjs(card.effectiveStart).isBefore(dayjs()) || dayjs(card.effectiveStart).isSame(dayjs(), 'day');
  return started ? { all: false, endOnly: true, readOnly: false } : { all: true, endOnly: false, readOnly: false };
}

/** Builds the react-hook-form default values for either a fresh create or an existing card. */
function buildDefaultValues(card: CppRateCard | null): FormValues {
  return {
    partnerId: card?.partnerId ?? '',
    currency: card?.currency ?? 'AUD',
    pricingModel: card?.pricingModel ?? 'FLAT',
    effectiveStart: card ? dayjs(card.effectiveStart).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
    effectiveEnd: card?.effectiveEnd ? dayjs(card.effectiveEnd).format('YYYY-MM-DD') : '',
  };
}

/**
 * Create/edit dialog for a CPP rate card, including tier configuration.
 * Partners are sourced from `useCppPartners`; Partner Type is display-only and derived
 * from whichever partner is currently selected in the form.
 */
export default function RateCardDialog({ open, onClose, card }: RateCardDialogProps) {
  const isEdit = card !== null;
  const edit = editability(card);

  // Creating (or editing a DRAFT) keeps/produces DRAFT status on this button's action, so label
  // it "Save as draft"; editing an already-ACTIVE card keeps it ACTIVE, so plain "Save" applies.
  const saveLabel = !isEdit || card?.status === 'DRAFT' ? 'Save as draft' : 'Save';

  const { data: partnersData } = useCppPartners();
  const partners = useMemo(() => partnersData?.result ?? [], [partnersData]);

  const { control, handleSubmit, watch, reset } = useForm<FormValues>({
    defaultValues: buildDefaultValues(card),
    mode: 'onBlur',
  });

  const pricingModel = watch('pricingModel');
  const partnerId = watch('partnerId');

  const [tiers, setTiers] = useState<Array<CppRateTier>>(card?.tiers ?? [FLAT_TIER]);
  const [tierError, setTierError] = useState<string | null>(null);
  // Surfaces submit/mutation failures inline (MUI Alert) instead of a snackbar, so the error
  // is anchored to the dialog the user is actively working in. `detail` carries an optional
  // second line, e.g. the conflicting card's effective date window on an overlap 409.
  const [submitError, setSubmitError] = useState<{ message: string; detail?: string } | null>(null);

  const createMutation = useCreateCppRateCard();
  const updateMutation = useUpdateCppRateCard();
  const activateMutation = useActivateCppRateCard();

  // Tracks the id of a card created in this dialog session, so a retry after a failed
  // activation (e.g. 409 overlap) updates/activates that card instead of creating a duplicate.
  // Held as a ref (read synchronously across awaits inside onSubmit) mirrored into state so the
  // UI can react to it (the Partner select is locked once a draft exists — see `partnerLocked`).
  const createdIdRef = useRef<string | null>(null);
  const [createdDraftId, setCreatedDraftId] = useState<string | null>(null);

  // Reset local state whenever the dialog opens (covers switching between create/edit or cards).
  useEffect(() => {
    if (open) {
      reset(buildDefaultValues(card));
      setTiers(card?.tiers ?? [FLAT_TIER]);
      setTierError(null);
      setSubmitError(null);
      createdIdRef.current = null;
      setCreatedDraftId(null);
    }
  }, [open, card, reset]);

  // FLAT collapses to a single unbounded tier whenever the pricing model switches.
  useEffect(() => {
    if (pricingModel === 'FLAT' && tiers.length !== 1) {
      setTiers([{ minPoints: 0, maxPoints: null, cpp: tiers[0]?.cpp ?? 0 }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricingModel]);

  // Partner Type is derived from whichever partner is currently selected (display-only).
  const selectedPartnerType: CppPartnerType | null = useMemo(
    () => partners.find((p) => p.id === partnerId)?.partnerType ?? null,
    [partners, partnerId],
  );

  const updateTier = (idx: number, patch: Partial<CppRateTier>) => {
    setTiers((prev) => prev.map((t, i) => (i === idx ? { ...t, ...patch } : t)));
  };

  const addTier = () => {
    setTiers((prev) => {
      const last = prev[prev.length - 1];
      // The previous tier must be bounded before a new tier can follow it. If it's still
      // open-ended, bound it to its own minPoints (a single-point band the user then widens).
      const prevMax = last?.maxPoints != null ? last.maxPoints : (last?.minPoints ?? 0);
      const bounded = prev.map((t, i) =>
        i === prev.length - 1 && t.maxPoints === null ? { ...t, maxPoints: prevMax } : t,
      );
      // New tier starts exactly one above the previous tier's (now concrete) max — contiguous, no gap.
      return [...bounded, { minPoints: prevMax + 1, maxPoints: null, cpp: 0 }];
    });
  };

  const removeTier = (idx: number) => {
    setTiers((prev) => prev.filter((_, i) => i !== idx));
  };

  /** Validates tiers client-side, then creates/updates (and optionally activates) the rate card. */
  const onSubmit = async (values: FormValues, thenActivate: boolean) => {
    setSubmitError(null);

    const error = validateTiersClient(tiers, values.pricingModel);
    if (error) {
      setTierError(error);
      return;
    }
    setTierError(null);

    // The start date is required and must parse: the field is clearable, so a blank/invalid value
    // would otherwise make dayjs(...).toISOString() below throw a RangeError outside this handler.
    // The `required` rule on the field normally blocks submission first; this is a defensive guard.
    if (!values.effectiveStart || !dayjs(values.effectiveStart).isValid()) {
      setSubmitError({ message: 'Effective start date is required.' });
      return;
    }

    // An empty effectiveEnd means open-ended (allowed). Equal dates are allowed too — only a
    // strictly earlier end date is rejected. Mirrors the backend's own validation.
    if (values.effectiveEnd && dayjs(values.effectiveEnd).isBefore(dayjs(values.effectiveStart), 'day')) {
      setSubmitError({ message: 'Effective end date must be on or after the effective start date.' });
      return;
    }

    const effectiveEndIso = values.effectiveEnd ? dayjs(values.effectiveEnd).startOf('day').toISOString() : null;
    const fullBody = {
      currency: values.currency,
      pricingModel: values.pricingModel,
      effectiveStart: dayjs(values.effectiveStart).startOf('day').toISOString(),
      effectiveEnd: effectiveEndIso,
      tiers,
    };

    // Tracks whether the save (create/update) phase completed before activation was attempted.
    // Lets the catch below distinguish "the save itself failed" from "the draft saved fine but
    // activation failed" (e.g. an overlap 409), so the inline error can say so explicitly.
    let savedOk = false;
    try {
      let savedId = card?.id;
      if (isEdit) {
        // Started-ACTIVE cards: the backend only permits the effective end date to change and
        // rejects the PATCH (409) if any other field is present, even unchanged. Send only what's editable.
        const updateBody = edit.endOnly ? { effectiveEnd: effectiveEndIso } : fullBody;
        // When this save is immediately followed by activation, suppress the intermediate
        // "updated successfully" toast — the activate mutation reports the final outcome
        // (success or error) so the user isn't shown a misleading success toast first.
        await updateMutation.mutateAsync({
          id: card.id,
          body: updateBody,
          showSuccessToast: !thenActivate,
          showErrorToast: false,
        });
      } else if (createdIdRef.current) {
        // A previous submit in this session already created the card (e.g. it was created but then
        // activation failed with a 409). Don't create a second one — instead UPDATE that draft with
        // the current form values, so edits made before retrying (e.g. a new start date to resolve
        // the overlap) are actually persisted. A freshly-created card is always DRAFT, so a full
        // update is valid here.
        savedId = createdIdRef.current;
        await updateMutation.mutateAsync({
          id: savedId,
          body: fullBody,
          showSuccessToast: !thenActivate,
          showErrorToast: false,
        });
      } else {
        // Same suppression logic as the update case above for the save-and-activate flow.
        const created = await createMutation.mutateAsync({
          body: { partnerId: values.partnerId, ...fullBody },
          showSuccessToast: !thenActivate,
          showErrorToast: false,
        });
        savedId = created.result.id;
        createdIdRef.current = savedId;
        // Mirror into state so the Partner select locks immediately (its value can no longer be
        // persisted — the retry path issues an UPDATE, which cannot change partnerId).
        setCreatedDraftId(savedId);
      }
      // The save (create/update) phase is now done; anything thrown past here is an activation failure.
      savedOk = true;
      if (thenActivate && savedId) {
        // Keep the activation success snackbar on (positive confirmation of the final outcome);
        // only the error is suppressed in favour of the dialog's inline Alert.
        await activateMutation.mutateAsync({ id: savedId, showErrorToast: false });
      }
      onClose();
    } catch (err) {
      // Surface the failure inline (MUI Alert) instead of a snackbar — the mutation hooks'
      // error snackbars are suppressed for dialog-driven actions. Keep the dialog open so the
      // user can adjust (e.g. fix an overlapping date range) and retry. Do not rethrow / close.
      // When the save succeeded but activation failed, make that explicit: the draft is already
      // persisted (and createdIdRef now points at it, so a retry activates rather than re-creates).
      const savedButActivateFailed = savedOk && thenActivate;
      setSubmitError(toSubmitError(err, savedButActivateFailed));
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending || activateMutation.isPending;

  // The Partner select is locked whenever the partner can no longer be persisted: in edit mode
  // (partnerId is immutable server-side), when the form is read-only, and — crucially — once a
  // draft has been created in this session, since the retry path issues an UPDATE that cannot
  // change partnerId. Without this lock, a partner change after a failed create+activate would be
  // silently dropped on retry.
  const partnerLocked = isEdit || edit.readOnly || createdDraftId !== null;

  // Selected partner's display name, shown as a subtitle under the dialog title once chosen.
  const selectedPartnerName = partners.find((p) => p.id === partnerId)?.name ?? null;

  // Block Escape/backdrop dismissal while a save/activate mutation is in flight. Dialog-driven
  // mutations suppress their error snackbars in favour of the inline Alert, so an accidental close
  // during a pending request could silently swallow a late failure. Mirrors the Cancel button's
  // `disabled={isPending}`.
  const handleDialogClose = () => {
    if (isPending) {
      return;
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Typography variant="overline" color="text.secondary" display="block">
          Configuration
        </Typography>
        <Typography variant="h6" component="div">
          {isEdit ? `Edit Rate Card ${card.reference}` : 'Create New Rate Card'}
        </Typography>
        {selectedPartnerName && (
          <Typography variant="body2" color="text.secondary">
            {selectedPartnerName}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {submitError && (
          <Alert severity="error" onClose={() => setSubmitError(null)} sx={{ mb: 2 }}>
            {submitError.message}
            {submitError.detail && (
              <Box component="span" sx={{ display: 'block', mt: 0.5, fontSize: '0.85rem' }}>
                {submitError.detail}
              </Box>
            )}
          </Alert>
        )}
        <Box
          display="grid"
          gridTemplateColumns={{ xs: '1fr', md: '1fr 1.2fr' }}
          gap={2}
          alignItems="start"
          sx={{ mt: 0.5 }}
        >
          {/* Left column: Basic Information + Contract Dates cards. */}
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <InfoOutlinedIcon fontSize="small" color="action" />
                <Typography variant="overline">Basic Information</Typography>
              </Stack>

              <Stack spacing={1.5}>
                <Select
                  control={control}
                  name="partnerId"
                  muiLabel="Partner"
                  disabled={partnerLocked}
                  rules={{ required: 'Partner is required' }}
                >
                  {partners.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name}
                    </MenuItem>
                  ))}
                </Select>

                {/* Partner Type is display-only, driven by whichever partner is currently selected. */}
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    Partner Type
                  </Typography>
                  <Chip label={selectedPartnerType ?? 'Not set'} size="small" />
                </Stack>

                <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={1.5} alignItems="center">
                  <Select control={control} name="currency" muiLabel="Currency" disabled={!edit.all}>
                    <MenuItem value="AUD">AUD ($)</MenuItem>
                    <MenuItem value="USD">USD ($)</MenuItem>
                    <MenuItem value="NZD">NZD ($)</MenuItem>
                  </Select>

                  <Controller
                    control={control}
                    name="pricingModel"
                    render={({ field }) => (
                      <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={field.value}
                        onChange={(_, v) => v && field.onChange(v)}
                        disabled={!edit.all}
                      >
                        <ToggleButton value="FLAT">Flat</ToggleButton>
                        <ToggleButton value="TIERED">Tiered</ToggleButton>
                      </ToggleButtonGroup>
                    )}
                  />
                </Box>
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <CalendarMonthIcon fontSize="small" color="action" />
                <Typography variant="overline">Contract Dates</Typography>
              </Stack>

              <Stack spacing={1.5}>
                <Input
                  control={control}
                  name="effectiveStart"
                  muiLabel="Effective Start"
                  type="date"
                  fullWidth
                  disabled={!edit.all}
                  rules={{ required: 'Effective start date is required.' }}
                />
                <Input
                  control={control}
                  name="effectiveEnd"
                  muiLabel="Effective End"
                  type="date"
                  fullWidth
                  disabled={edit.readOnly}
                />
              </Stack>
            </Paper>
          </Stack>

          {/* Right column: Tier Configuration card. */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="overline" display="block">
              Tier Configuration
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Define point thresholds and Cost-Per-Point (CPP)
            </Typography>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Min Points</TableCell>
                  <TableCell>Max Points</TableCell>
                  <TableCell>CPP (cents)</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tiers.map((tier, idx) => {
                  const isLast = idx === tiers.length - 1;
                  return (
                    // eslint-disable-next-line react/no-array-index-key
                    <TableRow key={idx}>
                      <TableCell sx={{ verticalAlign: 'top' }}>
                        <TextField
                          type="number"
                          size="small"
                          value={tier.minPoints}
                          onChange={(e) => updateTier(idx, { minPoints: Number(e.target.value) })}
                          disabled={pricingModel === 'FLAT' || !edit.all}
                        />
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'top' }}>
                        <TextField
                          type="number"
                          size="small"
                          value={tier.maxPoints ?? ''}
                          placeholder={isLast ? 'Unlimited' : ''}
                          onChange={(e) =>
                            updateTier(idx, { maxPoints: e.target.value === '' ? null : Number(e.target.value) })
                          }
                          disabled={pricingModel === 'FLAT' || !edit.all}
                        />
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'top' }}>
                        <TextField
                          type="number"
                          size="small"
                          value={tier.cpp}
                          inputProps={{ step: 0.01, min: 0 }}
                          onChange={(e) => updateTier(idx, { cpp: Number(e.target.value) })}
                          disabled={!edit.all}
                          slotProps={{
                            input: {
                              endAdornment: <InputAdornment position="end">¢</InputAdornment>,
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                        <IconButton
                          size="small"
                          onClick={() => removeTier(idx)}
                          disabled={pricingModel === 'FLAT' || tiers.length === 1 || !edit.all}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            {pricingModel === 'TIERED' && edit.all && (
              <Button size="small" variant="outlined" onClick={addTier} sx={{ mt: 1.5 }}>
                + Add New Tier
              </Button>
            )}
            {tierError && (
              <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                {tierError}
              </Typography>
            )}
          </Paper>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {isEdit && card.updatedAt && (
          <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
            Last edited {dayjs(card.updatedAt).fromNow()}
          </Typography>
        )}
        <Button variant="text" onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        {!edit.readOnly && (
          <Button variant="outlined" onClick={handleSubmit((v) => onSubmit(v, false))} disabled={isPending}>
            {saveLabel}
          </Button>
        )}
        {/* Activate is only offered for a DRAFT (create, or editing an existing draft). */}
        {(!isEdit || card.status === 'DRAFT') && (
          <Button variant="contained" onClick={handleSubmit((v) => onSubmit(v, true))} disabled={isPending}>
            {isEdit ? 'Save & Activate Rate Card' : 'Create & Activate'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
